import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { useCreateCardApiV1CardsPost } from '#/shared/api/generated/billing/theCargoBilling'
import type {
  BillingAddress,
  CreditCardSave,
} from '#/shared/api/generated/billing/models'
import { toast } from '#/shared/ui/sonner'

export const ADD_CARD_TOAST_KEYS = {
  success: 'addCard.toast.success',
  error: 'addCard.toast.error',
} as const

const CARDS_QUERY_PREFIX = '/api/v1/cards'

export interface AddCardFormState {
  firstName: string
  lastName: string
  nickname: string
  isDefault: boolean
}

export interface BillingAddressForm {
  address: string
  apartment: string
  city: string
  state: string
  zip: string
  country: string
}

export interface TokenizedCard {
  paymentToken: string
  expMonth: number
  expYear: number
}

export interface UseAddCardArgs {
  customerId: string | undefined
  customerEmail: string | undefined
  onSaved: () => void
}

const EMPTY_FORM: AddCardFormState = {
  firstName: '',
  lastName: '',
  nickname: '',
  isDefault: false,
}

const EMPTY_BILLING: BillingAddressForm = {
  address: '',
  apartment: '',
  city: '',
  state: '',
  zip: '',
  country: 'US',
}

function billingAddressPayload(
  billing: BillingAddressForm,
): BillingAddress | undefined {
  const address: BillingAddress = {
    address: billing.address.trim() || undefined,
    apartment: billing.apartment.trim() || undefined,
    city: billing.city.trim() || undefined,
    state: billing.state.trim() || undefined,
    zip: billing.zip.trim() || undefined,
    country: billing.country.trim() || undefined,
  }
  const hasValue = Object.values(address).some(
    (value) => value !== undefined && value !== 'US',
  )
  return hasValue ? address : undefined
}

function buildCardSave(
  args: UseAddCardArgs,
  form: AddCardFormState,
  billing: BillingAddressForm,
  card: TokenizedCard,
): CreditCardSave {
  return {
    payment_token: card.paymentToken,
    exp_month: card.expMonth,
    exp_year: card.expYear,
    customer_id: args.customerId,
    customer_email: args.customerEmail,
    cardholder_first_name: form.firstName.trim() || undefined,
    cardholder_last_name: form.lastName.trim() || undefined,
    nickname: form.nickname.trim() || undefined,
    is_default: form.isDefault,
    billing_address: billingAddressPayload(billing),
  }
}

export interface AddCardFields {
  form: AddCardFormState
  setField: <TKey extends keyof AddCardFormState>(
    key: TKey,
    value: AddCardFormState[TKey],
  ) => void
  billing: BillingAddressForm
  setBillingField: <TKey extends keyof BillingAddressForm>(
    key: TKey,
    value: BillingAddressForm[TKey],
  ) => void
}

export function useAddCardFields(): AddCardFields {
  const [form, setForm] = useState<AddCardFormState>(EMPTY_FORM)
  const [billing, setBilling] = useState<BillingAddressForm>(EMPTY_BILLING)

  const setField = <TKey extends keyof AddCardFormState>(
    key: TKey,
    value: AddCardFormState[TKey],
  ) => setForm((prev) => ({ ...prev, [key]: value }))

  const setBillingField = <TKey extends keyof BillingAddressForm>(
    key: TKey,
    value: BillingAddressForm[TKey],
  ) => setBilling((prev) => ({ ...prev, [key]: value }))

  return { form, setField, billing, setBillingField }
}

export interface SaveCard {
  save: (
    args: UseAddCardArgs,
    fields: AddCardFields,
    card: TokenizedCard,
  ) => Promise<void>
  isSaving: boolean
}

export function useSaveCard(): SaveCard {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const createCard = useCreateCardApiV1CardsPost()

  const save = async (
    args: UseAddCardArgs,
    fields: AddCardFields,
    card: TokenizedCard,
  ) => {
    const payload = buildCardSave(args, fields.form, fields.billing, card)
    await createCard.mutateAsync({ data: payload })
    await queryClient.invalidateQueries({ queryKey: [CARDS_QUERY_PREFIX] })
    toast.success(t(ADD_CARD_TOAST_KEYS.success))
    args.onSaved()
  }

  return { save, isSaving: createCard.isPending }
}
